import { SalesTrailerType } from './internal/lane-tool/lane-tool.service';
import { LoHiMappableDriver } from './map/map.service';

export interface LoHiLoadsListItem {
  id: string;
  uniqueId: string;
  name: string;
  phone: string;
  source: string;
  description: string;
  productName: string;
  poNumber: string;
  bolNumber: string;
  actualLoadWeight: number;
  waypointLongitude: number;
  waypointLatitude: number;
  waypointTimestamp: string;
  needsAdditionalInfo: string;
  needsAdditionalUpload: string;
  completedTimestamp: string;
  isAudited: boolean;
  isInvoiced: boolean;
  isReadyForInvoicing: boolean;
  onShift: boolean;
  partialLoad: boolean;
  driverId: string;
  lateOrder: number;
  linehaulTotal: number;
  deadheadTotal: number;
  unloadingTotal: number;
  loadingTotal: number;
  detentionTotal: number;
  accessorialTotal: number;
  approvedTimestamp: string;
  ticketAge: number;
}

export interface LoHiAdminLoadsBillingListItem {
  id: string;
  uniqueId: string;
  name: string;
  phone: string;
  source: 'sandi' | 'hauli' | 'reload' | 'lohi';
  needsAdditionalInfo: string[];
  needsAdditionalUpload: string[];
  completedTimestamp: string;
  isAudited: boolean;
  isInvoiced: boolean;
  isDisputed: boolean;
  isReadyForInvoicing: boolean;
  paidAt: string;
  approvedAt: string;
  ticketAge: number;
  linehaulTotal: number;
  deadheadTotal: number;
  detentionTotal: number;
  accessorialTotal: number;
  sandiMeta?: { isBoxMove: boolean };
  readyToInvoiceAt?: string;
  originAddress?: string;
  destinationAddress?: string;
  settlementStatementId: number | null;
  settledAt: string | null;
  bolNumber: string | null;
  availableForApproval: boolean;
  pushbackReason: string | null;
}

export interface UserInvite {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  permissions: ('driver' | 'admin')[];
  password: string;
  cut: number;
  cdl: string;
  eldGroupId: number;
}

export interface CreateDriverForApplicantRequestRetool {
  driverName: string;
  phoneNumber: string;
  email: string;
  password?: string;
  is5FTourDriver: boolean;
  isAdmin: boolean;
}

export interface DriverActionNeeded {
  phoneNumber: string;
  actionNeededTitle: string;
  actionNeededDescription: string;
  type: 'Online' | 'Unreachable';
}

export interface LoHiShiftListItem {
  id: string;
  driverName: string;
  phoneNumber: string;
  startTimestamp: string;
  endTimestamp: string;
  estimatedTurns: number;
  estimatedTurnsLower: number;
  estimatedTurnsUpper: number;
  isBoxShift: boolean;
  estimatedEarningsCents: number;
  estimatedEarningsCentsLower: number;
  estimatedEarningsCentsUpper: number;
  adjustedEstimatedEarningsCents: number;
  adjustedEstimatedEarningsCentsLower: number;
  adjustedEstimatedEarningsCentsUpper: number;
  rejectedCount: number;
  regionId: number;
  unassignedCount: number;
}

export interface DriverPriority {
  priorityId: number;
  priorityStartedAt: string;
  priorityStartedBy: string;
  priorityStartedReason?: string;
}

export interface ActiveDriver {
  id: string;
  driverName: string;
  driverTruck: number;
  phoneNumber: string;
  actionNeeded: DriverActionNeeded;
  permissions: string[];
  inProgressShift: LoHiShiftListItem;
  upcomingShift: LoHiShiftListItem;
  previousShift: LoHiShiftListItem;
  isVerified: boolean;
  podNeededCount: number;
  currentShiftLoadCount: number;
  currentShiftLoadCountPartials: number;
  driverCut: number;
  driverTarget: number;
  cdl: string;
  employeeId: string;
  priorityDriver?: DriverPriority;
  allowShiftBooking: boolean;
  mappable: LoHiMappableDriver;
  removeTrailerFee: boolean;
  userTakeRate?: number;
  userTakeRateName?: string;
  currentPoints: number;
  targetPoints: number;
  spendingLimit: number | null;
}

export interface Improvement {
  lostCents: number;
  rank: number;
  suggestion: string;
}

export interface PendingDriver {
  id: string;
  companyId: string;
  driverName: string;
  phoneNumber: string;
  permissions: string[];
  createdAt: string;
}

export function isActiveDriver(d: ActiveDriver | PendingDriver): d is ActiveDriver {
  return (d as ActiveDriver).isVerified !== undefined;
}

export interface DriverListResponse {
  activeDrivers: ActiveDriver[];
  pendingDrivers: PendingDriver[];
}

export interface Unit {
  name: string;
  abbreviation: string;
}

export interface Product {
  name: string;
  unit: Unit;
}

export interface Task {
  action: string;
  product: Product;
  quantity: number;
  actualQuantity: number;
  bolNumber: string;
  poNumber: string;
}

export interface Stop {
  locationName: string;
  ownerName: string;
  radius: number;
  longitude: number;
  latitude: number;
  tasks: Task[];
  directions: string;
}

export interface LoHiUpload {
  id: string;
  loadId: string;
  fileName: string;
  uploadedBy: string;
  uploadedTimestamp: string;
  category: string;
  bucketName: string;
  shareableUrl: string;
  originalFilename: string;
}

export interface OvernightLocation {
  id: string;
  name: string;
  category: string;
  latitude: number;
  longitude: number;
  travelDistanceMiles: number;
  travelDistanceMinutes: number;
  amenities: string[];
}

export interface EndOfShiftInfo {
  estHosRemainingAfterLoadCompleteMinutes: number;
  recommendedOvernightLocation: OvernightLocation[];
}

export interface LohiLoad {
  id: string;
  uniqueId: string;
  source: string;
  needsConfirmation: boolean;
  description: string;
  truckName: string;
  trailerName: string;
  canUploadDocuments: boolean;
  canEditOrder: boolean;
  canCompleteManually: boolean;
  stops: Stop[];
  uploads: LoHiUpload[];
  needsAdditionalInfo: string;
  needsAdditionalUpload: string;
  acceptedTimestamp: string;
  completedTimestamp: string;
  driverName: string;
  driverPhone: string;
  estimatedDurationMinutes: number;
  dispatchTimestamp: string;
  waypointLongitude: number;
  waypointLatitude: number;
  waypointTimestamp: string;
  loaded: boolean;
  eta: number;
  etaStatus: string;
  endOfShiftInfo: EndOfShiftInfo;
  estimatedMileage: number;
  estimatedOrderTotalCents: number;
  adjustedEstimatedOrderTotalCents: number;
  likelyLastLoadOfShift: boolean;
  partialLoad: boolean;
  minutesLeftOnShiftAfterLoad: number;
  estimatedMinutesToMine: number;
  estimatedMinutesAtMine: number;
  estimatedMinutesToFrac: number;
  estimatedMinutesAtFrac: number;
  deadheadMiles: number;
  loadedMiles: number;
}

export type UploadImageCategory = 'other_image' | 'ticket_image' | 'bol_image' | '';

export interface LohiAsset {
  id: string;
  name: string;
  actualLoadWeight: number;
}

export interface EditLoadRequest {
  bolNumber: string;
  actualQuantity: number;
  truckId?: string;
  trailerId?: string;
  assets: LohiAsset[];
  ticketNumber?: string;
  returnTrailer?: string;
}

export interface LoHiTruck {
  truckId: string;
  truckName: string;
  inUse: boolean;
  assignedTo: string;
  assignedToPhone: string;
  truckOwner: string;
  lastUsed: LastUsed;
  maintenanceEvents: MaintenanceEvent[];
  truckLessee: string;
  truckStatus: string;
}

export interface LastUsed {
  lastUpdated: string;
  orderId: number;
  uniqueOrderId: string;
  lastLocation: number[];
  userPhone: string;
  userName: string;
  userId: string;
  lastUsed: LastUsed;
}

export interface MaintenanceEvent {
  createdAt: string;
  description: string;
}

export interface LoHiTrailer {
  trailerId: string;
  trailerName: string;
  inUse: boolean;
  trailerOwner: string;
  trailerLessee: string;
  maintenanceEvents: MaintenanceEvent[];
  trailerStatus: string;
  lastUsed: LastUsed;
}

export interface LoHiLoadEditableInfo {
  bolNumber: string;
  actualQuantity: number;
  truckEnabled: boolean;
  truckAsset: LoHiTruck;
  trailerEnabled: boolean;
  trailerAsset: LoHiTrailer;
  assets: LohiAsset[];
  unit: {
    name: string;
    abbreviation: string;
  };
  returnTrailerEnabled: boolean;
  returnTrailer: string | null;
  ticketNumberEnabled: string | null;
  ticketNumber: string | null;
}

export interface Stats {
  drivers: DriverStat[];
}

export interface DriverStat {
  UserId: string;
  user_name: string;
  user_phone: string;
  dpi: number;
  learningDPI: boolean;
  shift_count: number;
  turns_completed: number;
  turns_optimal: number;
  total_earnings: number;
  lost_earnings: number;
  avgLostEarnings: number;
  avgEarnings: number;
  avgRevenue: number;
  coachingMessage: string;
  improvementsDays: number;
  improvementsLostEarnings: number;
  driver_earnings_per_day: EarningsPerDay[];
  improvements: Improvement[];
  coachingCategory: number;
  lastCoachedAt: string;
  leaderboardCounts: { completedAt: string; winner: boolean }[];
  totalTimeLost: number;
}

export interface EarningsPerDay {
  date: string;
  duration: number;
  earnings: number;
  lostEarnings: number;
  loadsCompleted: number;
  loadsLost: number;
  endedShiftEarly: boolean;
  endedShiftEarlyMinutes: number;
  excessDeadhead: number;
  excessLineHaul: number;
  excessMineTime: number;
  excessWellTime: number;
  flickedMinutes: number;
  rejectedMinutes: number;
  timeToAccept: number;
}

export interface TotalStats {
  loads_lost: number;
  turn_rate: number;
  lost_earnings: number;
  avgEarnings: number;
  totalEarnings: number;
  earningsPerDay: EarningsPerDay[];
}

export interface PieStats {
  sleepers: PieData;
  dayCabs: PieData;
}

export interface PieData {
  totalEarnings: number;
  missedEarnings: number;
}

export interface LoHiAggregateEarnings {
  totalEarnings: number;
  missedEarnings: number;
  averagePerDayEarnings: number;
  missedAveragePerDayEarnings: number;
}

export interface CompanyMetrics {
  earningsAggregateByCab: {
    sleeper: LoHiAggregateEarnings;
    dayCab: LoHiAggregateEarnings;
  };
  totalEarnings: number;
  missedEarnings: number;
  missedLoads: number;
  averageTurnRate: number;
  averageRevenuePerShift: number;
  daysOfHistory: number;
  earningsByPhone: number;
  averageDriverEarningsPerShift: number;
}

export interface CompanyDriverMetric {
  driverCurrentTier: DriverCurrentTier[];
}

export interface DriverCurrentTier {
  userID: string;
  name: string;
  totalPoints: number;
  gainLostPoints: number;
  improvementBar: boolean;
  tierType: string;
  tierName: string;
}

export interface DriverMetricDetail {
  metricDetail: {
    userID: string;
    name: string;
    totalPoints: number;
    tierType: string;
    tierName: string;
    tierTakeRate: number;
    tierRequiredPoints: number;
    pointsToNextTier: number;
    nextTierType: string;
    nextTierName: string;
    nextTierTakeRate: number;
    pointsToPreviousTier: number;
    previousTierType: string;
    previousTierName: string;
    previousTierTakeRate: number;
    userGroupedEvents: UserGroupedEvent[];
    waysToBlackTier: TierSimulationSummary;
  };
}

export interface TierSimulationSummary {
  title: string;
  subtitle: string;
  minTurnRate: number;
  maxTurnRate: number;
  minHitchLength: number;
  maxHitchLength: number;
  simulationExplanations: TierSimulationExplanation[];
}

export interface TierSimulationExplanation {
  turnRate: number;
  hitchLength: number;
  isNightShift: boolean;
  title: string;
  subtitle: string;
  descriptions: string[];
}

export interface UserGroupedEvent {
  type: string;
  name: string;
  sumPoints: number;
  eventCount: number;
}

export interface CompanyRevenueEntry {
  date: string;
  year: number;
  month: number;
  day: number;
  totalEarnings: number;
  missedEarnings: number;
}

export interface CompanyRevenueResponse {
  entries: CompanyRevenueEntry[];
  trend: string;
}

export interface CompanyShiftAverageRevenue {
  date: string;
  year: number;
  month: number;
  day: number;
  averageEarnings: number;
  averageMissedEarnings: number;
  averageTurnRate: number;
}

export interface CompanyShiftAverageResponse {
  entries: CompanyShiftAverageRevenue[];
  trend: string;
}

export interface LostEarningsByDriver {
  userId: number;
  lohiUserId: string;
  name: string;
  missedEarnings: number;
}

export interface CompanyShiftsPerDay {
  date: string;
  year: number;
  month: number;
  day: number;
  shiftCount: number;
}

export interface CompanyShiftsPerDayResponse {
  entries: CompanyShiftsPerDay[];
}

export interface CompanyLoadsPerDay {
  date: string;
  year: number;
  month: number;
  day: number;
  loadsCompleted: number;
  loadsMissed: number;
}

export interface CompanyLoadsPerDayResponse {
  entries: CompanyLoadsPerDay[];
}

export interface ReportImprovements {
  lostEarningsCents: number;
  numDays: number;
  numShifts: number;
  daysBack: number;
  improvements: Improvement[];
}

export interface ELDGroupUser {
  id: string;
  name: string;
  phone: string;
}

export interface ELDGroup {
  id: number;
  name: string;
  providerName: string;
  createdAt: string;
  updatedAt: string;
  drivers: ELDGroupUser[];
}

export interface ELDGroupResponse {
  groups: ELDGroup[];
  unassignedDrivers: ELDGroupUser[];
}

export interface LeaderboardPoolForAdmin {
  id: number;
  name: string;
  winnerCount: number;
  bonusCents: number;
  prize: LeaderboardForAdminPrize;
  calculation_type: string;
  durationHours: number;
  showBonusCents: boolean;
  showScore: boolean;
  leaderboardCount: number;
  leaderboard: LeaderboardForAdmin;
}

export interface LeaderboardForAdmin {
  id: number;
  winnerCount: number;
  createdAt: string;
  endAt: string;
  completedAt: string;
  ranks: LeaderboardForAdminRank[];
  isEnded: boolean;
  previousId: number;
  nextId: number;
  prize: LeaderboardForAdminPrize;
  bonusCents: number;
}

export interface LeaderboardForAdminRank {
  userId: string;
  name: string;
  rank: number;
  carrierEarningsCents: number;
  receivedBonusCents: number;
  prize: LeaderboardForAdminPrize;
  qualifiesForPrize: boolean;
}

export interface LeaderboardForAdminPrize {
  id: number;
  name: string;
  url: string;
}

export interface LeaderboardPoolGroupDriver {
  id: string;
  name: string;
  phone: string;
}

export interface LeaderboardPoolDriversGroup {
  id: number;
  name: string;
  drivers: LeaderboardPoolGroupDriver[];
}

export interface LeaderboardPoolDriversGroupResponse {
  pools: LeaderboardPoolDriversGroup[];
  unassignedDrivers: LeaderboardPoolGroupDriver[];
}

export interface CarrierScorecardEntries {
  carriers: CarrierScorecardEntry[];
}

export interface CarrierScorecardEntry {
  carrierName: string | null;
  isYou: boolean;
  safetyScore: string | number;
  missedCentsAverage: number;
  underAllocationPercent: number;
  nightShiftsUnderAllocationPercent: number;
  weekendShiftsUnderAllocationPercent: number;
  missedShiftsTotal: number;
  rejectedLoadsTotal: number;
  prescheduledShiftsPercent: number;
  missedShiftsPercent: number;
  rejectedLoadsPercent: number;
  prescheduledNightShiftPercent: number;
  prescheduledWeekendShiftPercent: number;
}

export interface LoHiDriverShiftOverviews {
  entries: LoHiDriverShiftOverview[];
}

export interface LoHiDriverShiftOverview {
  timestamp: string;
  actualShiftCount: number;
  scheduledShiftCount: number;
  commitment: number;
  demand: number;
  belowCommitment: {
    index: number;
    message: string;
    timestamp: string;
  };
}

export interface DriverShiftOverviewRegion {
  id: number;
  name: string;
  overviews: LoHiDriverShiftOverview[];
}

export interface DriverShiftOverviewResponse {
  regions: DriverShiftOverviewRegion[];
}

export interface DayCommitment {
  requestId: number;
  year: number;
  month: number;
  day: number;
  dayOfWeek: string;
  originalRequest: number;
  originalResponse: number;
  latestResponse: number;
  currentRequest: number;
  supplyDeficit: number;
  actualAverageConcurrentDrivers: number;
  driverHourScore: number;
  holidayName: string;
  isToday?: boolean;
  isInThePast?: boolean;
  loadsPerShift?: number;
  noLoadsAvailableMessage?: string;
  noLoadsAvailableCreatedAt?: string;
}

export interface WeekCommitment {
  startsOn: string;
  endsAt: string;
  closed: boolean;
  days: DayCommitment[];
}

export interface RegionCommitments {
  id: number;
  name: string;
  weeks: WeekCommitment[];
}

export interface CommitmentsForCompanyResponse {
  regions: RegionCommitments[];
}

export interface MissedShifts {
  percentage: number;
  userId: string;
  userName: string;
}

export interface DeclinedLoads {
  percentage: number;
  userId: string;
  userName: string;
}

export interface BookedInAdvance {
  percentage: number;
  userId: string;
  userName: string;
}

export interface RejectedLoads {
  numberOfDays: number;
  driverStats: RejectedLoadsByUser[];
}

export interface RejectedLoadsByUser {
  userId: string;
  userName: string;
  userPhone: string;
  percentRejected: number;
}

export interface ShiftSchedulingStats {
  numberOfDays: number;
  driverStats: ShiftSchedulingStat[];
}

export interface ShiftSchedulingStat {
  userId: string;
  userName: string;
  userPhone: string;
  cancelPercentage: number;
  advanceBookingPercentage: number;
  dayOfPercentage: number;
}

export interface DriverSchedulePoolDriver {
  id: string;
  name: string;
  phone: string;
}

export interface DriverScheduleBlock {
  id: number;
  start: number;
  end: number;
  friendlyString: string;
}

export interface DriverSchedulePoolGroup {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  blocks: DriverScheduleBlock[];
  prettyPrintOffsetTimes: string;
  drivers: DriverSchedulePoolDriver[];
}

export interface DriverSchedulePoolResponse {
  groups: DriverSchedulePoolGroup[];
  unassignedDrivers: DriverSchedulePoolDriver[];
}

export interface LoHiOrderStatsRegion {
  id: number;
  name: string;
  upForGrabsCount: number;
  qualifiedUpForGrabsCount: number;
  medianSecondsToDispatch?: number;
  noLoadsAvailableMessage: string;
  noLoadsAvailableTitle: string;
  noLoadsAvailableCreatedAt: string;
  qualifiedNoLoadsAvailableMessage: string;
  qualifiedNoLoadsAvailableTitle: string;
  qualifiedNoLoadsAvailableCreatedAt: string;
  medianSecondsToDispatchByOrderCount: {
    medianSecondsToDispatch: number;
    orderCount: number;
    sampleSize: number;
    waitingDrivers: {
      phone: string;
      secondsWaiting: number;
    }[];
  }[];
}

export interface ListAvailableDriversResponseDriver {
  userId: string;
  phone: string;
  name: string;
  lastRegionId: number;
  lastRegionName: string;
}

export interface RecommendedShiftRegion {
  name: string;
  etaMinutes: number;
  recommendedShiftId: number;
  startRadiusMeters: number;
  startLongitude: number;
  startLatitude: number;
}

export interface RecommendedShiftBucket {
  startTimestamp: string;
  endTimestamp: string;
  availableShiftCount: number;
  leaderboardModifierText: string;
}

export interface LoHiRecommendedShiftDetail {
  id: number;
  startTimestamp: string;
  startWindowMinutes: number;
  endTimestamp: string;
  estimatedTurns: number;
  estimatedTurnsLower: number;
  estimatedTurnsUpper: number;
  estimatedDurationMinutes: number;
  checkinLng: number;
  checkinLat: number;
  checkinRadiusMeters: number;
  isBoxShift: boolean;
  estimatedEarningsCents: number;
  estimatedEarningsCentsLower: number;
  estimatedEarningsCentsUpper: number;
  adjustedEstimatedEarningsCents: number;
  adjustedEstimatedEarningsCentsLower: number;
  adjustedEstimatedEarningsCentsUpper: number;
  hseRequirements: string;
  leaderboardModifierText: string;
}

export interface NotificationType {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  enabled: boolean;
  provider: 'email' | 'twilio' | 'firebase';
}

export type CompletedLoadsSortables =
  | 'id'
  | 'unique_id'
  | 'user_name'
  | 'user_phone'
  | 'description'
  | 'product_name'
  | 'po_number'
  | 'bol_number'
  | 'actual_load_weight'
  | 'completed_timestamp'
  | 'is_ready_for_invoicing'
  | 'is_audited'
  | 'is_invoiced'
  | 'truck_name'
  | 'trailer_name'
  | 'linehaul_total_amount'
  | 'deadhead_total_amount'
  | 'unloading_total_amount'
  | 'accessorial_total_amount'
  | 'ticket_age'
  | 'linehaul_total_cents'
  | 'deadhead_total_cents'
  | 'accessorial_total_cents'
  | 'detention_total_cents'
  | 'needs_info'
  | 'needs_upload';

export interface Pagination {
  offset: number;
  limit: number;
  sort: CompletedLoadsSortables;
  order: 'ASC' | 'DESC';
  total: number;
}

export interface BillingOrdersResponse {
  loads: LoHiAdminLoadsBillingListItem[];
  page: Pagination;
}

export interface RevenueAllocationItem {
  id: string;
  name: string;
  previous: number;
  current: number;
  expectedEarnings: number;
  isPriority: boolean;
}

export interface RevenueAllocationResponse {
  isPossible: boolean;
  matchesDb: boolean;
  graphData: RevenueAllocationItem[];
  projectedLoads: number;
  validForLoadCount: number;
  totalRevenue: number;
  averageShiftEarnings: number;
}

export interface AutoDispatchStats {
  demandSupplyRatio: number;
  ordersCreatedForLoHi: number;
  pendingOrdersAtInsertion: number;
  urgentPendingOrdersAtInsertion: number;
  deadheadLinehaulRatio: number;
  correctedDeadheadLinehaulRatio: number;
  stdDevMappedDeadHeadMiles: number;
  stdDevMappedCorrectedDeadHeadMiles: number;
  meanMinutesToFirstDispatchInShift: number;
  stdDevMinutesToFirstDispatchInShift: number;
  outlierShifts: number;
  shiftsNeverDispatched: number;
}

export interface AutoDispatchStatsGraphData {
  demandSupplyRatio: number;
  createdAt: string;
  value: number;
}

export interface BillingSummary {
  data: BillingSummaryData;
}

export interface BillingSummaryData {
  missingPODCount: number;
  missingPODMoreThan24HrsCount: number;
  averageWorkingDaysToApprove: number;
}

export interface DriverCutGroup {
  id: number;
  CompanyID: string;
  name: string;
  cut: number;
  users: DriverCutGroupUser[];
  chargeTypes: DriverCutGroupChargeType[];
}

export interface DriverCutGroupUser {
  id: string;
  phone: string;
  name: string;
}

export interface DriverCutGroupChargeType {
  type: string;
  included: boolean;
}

export interface SettlementStatementSummary {
  id: number;
  date: string;
  year: number;
  month: number;
  day: number;
  loadCount: number;
  revenueInPennies: number;
  adjustmentInPennies: number;
  deductionsInPennies: number;
  takeRateInPennies: number;
  payableInPennies: number;
  driversTotalInPennies: number;
  carriersTotalInPennies: number;
  paidAt: Date;
  paymentIssue?: PaymentIssue | null;
}

export interface PaymentIssue {
  issue: string | null;
  createdAt?: string;
  respondedAt?: string | null;
  // Prefer responseText when provided by backend; fallback to resolution for compatibility
  responseText?: string | null;
  response?: string | null;
  resolution?: string | null;
  // The backend may send identifiers; keep optional for forward compatibility
  loadId?: string | null;
  settlementId?: number | null;
  issueTypeIds?: string[];
}

export interface SettlementStatementPaymentIssueType {
  id: string;
  displayName: string;
}

export interface SettlementStatementOrderDetailSiteDetail {
  siteTime: string;
  name: string;
  lngLat: [number, number];
}

export interface SettlementStatementOrderDetailLineItem {
  description: string;
  totalInPennies: number;
}

export interface SettlementStatementOrderDetail {
  id: string;
  propxTicketNumber: string | null;
  driverName: string;
  acceptTime: string;
  loadTotalInPennies: number;
  pickupLocation: SettlementStatementOrderDetailSiteDetail;
  dropoffLocation: SettlementStatementOrderDetailSiteDetail;
  trailerTimestamp: string;
  lineHaulMiles: number;
  lineItems: SettlementStatementOrderDetailLineItem[];
  bolNumber?: string;
}

export interface SettlementStatementDeductionLineItem {
  description: string;
  totalInPennies: number;
  isOnOrder: boolean;
}

export interface SettlementStatementAdjustmentItem {
  id: number;
  totalInPennies: number;
  userFacingComment: string;
}

export interface DailyJournalEntry {
  displayName: string;
  driverName: string;
  billingInterval: string;
  year: number;
  month: number;
  day: number;
  paidInPennies: number;
}

export interface TruckJournalEntry {
  vin: string;
  valueInPennies: number;
  driverName: string;
  year: number;
  month: number;
  day: number;
  paidInPennies: number;
}

export interface SettlementStatementEscrowPayout {
  id: number;
  totalPaid: number;
  carrierFacingNotes: string;
}

export interface SettlementStatementFile {
  id: number;
  displayName: string;
  fileName: string;
  publicUrl: string;
}

export interface SettlementStatement {
  company: {
    address: string;
    dotNumber: string;
    id: string;
    name: string;
  };
  statement: SettlementStatementDetails;
}

export interface SettlementStatementDirectDriverPayment {
  description: string;
  totalInPennies: number;
  userName: string;
}

export interface SettlementStatementFuelTransaction {
  id: number;
  time: string;
  totalInPennies: number;
}

export interface SettlementStatementDetails {
  id: number;
  year: number;
  month: number;
  day: number;
  paidAt: Date | string | null;
  totalCredits: number;
  totalOfAllLoads: number;
  totalPayable: number;
  totalDeductions: number;
  firstLoadDate: Date | string | null;
  lastLoadDate: Date | string | null;
  adjustment: SettlementStatementAdjustmentItem[];
  orders: SettlementStatementOrderDetail[];
  lineItems: SettlementStatementDeductionLineItem[];
  nonOrderLineItems: SettlementStatementDeductionLineItem[];
  escrowPayouts: SettlementStatementEscrowPayout[];
  dailyJournalEntries: DailyJournalEntry[];
  truckJournalEntries: TruckJournalEntry[];
  directDriverPaymentInfo: SettlementStatementDirectDriverPayment[];
  fuelTransactions: SettlementStatementFuelTransaction[];
  files: SettlementStatementFile[];
}

export interface StatementsOverview {
  averageByDateOverLast7Days: {
    averageTotalPerDay: number;
    averageTotalPerDayPerDriver: number;
    daysCount: number;
    randomDaysCount: number;
    totalPerDayPerDriverByRandomDaysCount: number;
  };
  escrowDetails: {
    driverBasedEscrow: number;
    escrowTotalOverride: number;
    lineItems: {
      name: string;
      count: number;
      totalInPennies: number;
    }[];
    maxEscrowInPennies: number;
    noTrailerEscrow: boolean;
  };
  depositCompletionPercentage: number;
  totalAssetDeposit: number;
  totalPaidDeposit: number;
  statements: SettlementStatementSummary[];
  totalPayableOver7Days: number;
  totalTrucksOver7Days: number;
  hasTruck: boolean;
}

export interface InsuredTruck {
  vin: string;
  valueInPennies: number;
  driverName: string;
  archivedAt: string;
}

export interface CompanyDailyBillingItems {
  displayName: string;
  driverId: string;
  driverName: string;
  billingInterval: 'work_day' | 'every_day';
  costInPennies: number;
}

export interface CompanyChargesDetails {
  insuredTrucks: InsuredTruck[];
  dailyBillingItems: CompanyDailyBillingItems[];
}

export type TaskActionV2 =
  | 'pickup'
  | 'dropoff'
  | 'backhaul_pickup'
  | 'backhaul_dropoff'
  | 'unknown'
  | 'boxPickup'
  | 'other'
  | 'assetPickup'
  | 'assetDropoff'
  | 'return';

export interface TaskV2 {
  action: TaskActionV2;
  product: {
    name: string;
    unit: {
      name: string;
      abbreviation: string;
    };
  };
  quantity: number;
  actualQuantity?: number;
  bolNumber?: string;
  poNumber?: string;
}

export interface StopV2 {
  locationName: string;
  ownerName: string;
  radius: number;
  longitude: number;
  latitude: number;
  entranceLongitude: number;
  entranceLatitude: number;
  tasks: TaskV2[];
  directions?: string;
  completed: boolean;
  estimatedMinutesTo?: number;
  estimatedMinutesAt?: number;
  firstTimeAtStop: boolean;
  longestPreviousVisitMinutes?: number;
  hseRequirements?: string;
  pickupNumber?: string;
  trailerNumber?: string;
}

export type Source = 'sandi' | 'hauli' | 'reload' | 'lohi';

export interface LohiLoadV2 {
  customerName: string;
  id: string;
  uniqueId: string;
  source: Source;
  needsConfirmation: boolean;
  description: string;
  truckName: string;
  trailerName: string;
  canUploadDocuments: boolean;
  canEditOrder: boolean;
  canCompleteManually: boolean;
  stops: StopV2[];
  uploads: {
    id: string;
    loadId: string;
    fileName: string;
    uploadedBy: string;
    uploadedTimestamp: string;
    category: string;
    bucketName: string;
    shareableUrl: string;
    originalFilename: string;
  }[];
  acceptedTimestamp: string;
  completedTimestamp: string;
  driverName: string;
  driverPhone: string;
  estimatedDurationMinutes: number;
  dispatchTimestamp: string;
  waypointLongitude: number;
  waypointLatitude: number;
  waypointTimestamp: string;
  eta: number;
  etaStatus: string;
  endOfShiftInfo?: {
    estHosRemainingAfterLoadCompleteMinutes: number;
    recommendedOvernightLocation: {
      id: string;
      name: string;
      category: string;
      latitude: string;
      longitude: string;
      travelDistanceMiles: string;
      travelDistanceMinutes: string;
      amenities: string[];
    }[];
  };
  estimatedMileage?: number;
  estimatedOrderTotalCents?: number;
  adjustedEstimatedOrderTotalCents?: number;
  likelyLastLoadOfShift: boolean;
  partialLoad: boolean;
  ticketNumber?: string;
  minutesLeftOnShiftAfterLoad: number;
  pickupMinutesUpdatedAgo?: number;
  dropoffTrucksOnLocation?: number;
  deadheadMiles?: number;
  loadedMiles?: number;
  leaderboardPoints?: {
    points: number;
    pointsModifier: number;
    pointText: string;
    modifierText: string;
  };
  minutesToAccept: number;
  loadWeightRestriction?: number;
  disptachPitches: string[];
  skipDocumentUpload: boolean;
  approvedAt?: string;
  ticketAge?: number;
  pricingEvents: {
    title: string;
    subtitle: string;
    estPrice: string;
  }[];
  earnings?: {
    linehaulTotal: number;
    deadheadTotal: number;
    unloadingDetentionTotal: number;
    loadingDetentionTotal: number;
    detentionTotal: number;
    accessorialTotal: number;
    bonusTotal: number;
    fuelTotal: number;
    totalAmount: number;
    cut: number;
    excludedChargeTypes: string[];
  };
  ffRewardsOrderData?: {
    points: number;
    description: string;
    completeBy: string;
  };
  billingInfo?: {
    needsAdditionalInfo: string[];
    needsAdditionalUpload: string[];
    isDisputed: boolean;
    isAudited: boolean;
    isInvoiced: boolean;
    isReadyForInvoicing: boolean;
    paidAt: string;
    billingAction: string;
    carrierPaidBy5F: boolean;
    carrierSubmitted?: string;
    carrierFunded?: string;
    billingTeamApproved?: string;
  };
  urgentInfo: {
    urgent: boolean;
    urgentRejectEndsShift: boolean;
    urgentRejectDriverPauseMinutes: number;
    urgentRejectText: string;
    urgentUnassignText: string;
  };
  referenceNumber?: string;
}

export function lohiLoadV2ToAdminBillingListItem(load: LohiLoadV2): LoHiAdminLoadsBillingListItem {
  return {
    id: load.id,
    uniqueId: load.uniqueId,
    name: load.driverName,
    phone: load.driverPhone,
    source: load.source,
    needsAdditionalInfo: load?.billingInfo?.needsAdditionalInfo || [],
    needsAdditionalUpload: load?.billingInfo?.needsAdditionalUpload || [],
    completedTimestamp: load.completedTimestamp,
    isDisputed: load?.billingInfo?.isDisputed || false,
    isAudited: load?.billingInfo?.isAudited || false,
    isInvoiced: load?.billingInfo?.isInvoiced || false,
    isReadyForInvoicing: load?.billingInfo?.isReadyForInvoicing || false,
    approvedAt: load.approvedAt,
    ticketAge: load.ticketAge,
    paidAt: load?.billingInfo?.paidAt,
    linehaulTotal: load.earnings.linehaulTotal,
    deadheadTotal: load.earnings?.deadheadTotal,
    detentionTotal: (load.earnings?.loadingDetentionTotal || 0) + (load.earnings?.unloadingDetentionTotal || 0),
    accessorialTotal:
      (load.earnings?.accessorialTotal || 0) + (load.earnings?.bonusTotal || 0) + (load.earnings?.fuelTotal || 0),
    settlementStatementId: null,
    settledAt: null,
    bolNumber: null,
    availableForApproval: false,
    pushbackReason: null,
  };
}

export interface LoHiAdminLoadsBillingListItem {
  id: string;
  uniqueId: string;
  name: string;
  phone: string;
  source: Source;
  needsAdditionalInfo: string[];
  needsAdditionalUpload: string[];
  completedTimestamp: string;
  isDisputed: boolean;
  isAudited: boolean;
  isInvoiced: boolean;
  isReadyForInvoicing: boolean;
  paidAt: string;
  linehaulTotal: number;
  deadheadTotal: number;
  detentionTotal: number;
  accessorialTotal: number;
  readyToInvoiceAt?: string;
  originAddress?: string;
  destinationAddress?: string;
  availableForApproval: boolean;
  paymentIssue?: PaymentIssue | null;
}

export interface LoHiAdminInProgressLoadListItem {
  id: string;
  uniqueId: string;
  name: string;
  phone: string;
  driverId: string;
  source: Source;
  description?: string;
  waypointLongitude?: string;
  waypointLatitude?: string;
  waypointTimestamp?: string;
  partialLoad: boolean;
  onShift: boolean;
  minutesLate?: number;
  delayedReason?: string;
  originSiteName?: string;
  destinationSiteName?: string;
}

export interface DashboardResponse {
  dashboardData: DashboardData[];
}

export interface DashboardData {
  name: string;
  phone: string;
  uniqueId: string | null;
  load: LohiLoadV2 | null;
  shiftEnd: Date | null;
}

export interface RewardTierHistory {
  name: string;
  takeRate: number;
  activeAt: string;
  displayName: string;
  details: string;
  points: number;
}

export interface DriverDetail {
  id: string;
  certs: number[];
  driverName: string;
  phoneNumber: string;
  actionNeeded?: DriverActionNeeded;
  permissions: string[];
  inProgressShift: LoHiRecommendedShiftDetail;
  upcomingShift: LoHiRecommendedShiftDetail;
  inProgressLoad: LohiLoad;
  podNeededCount: number;
  currentShiftLoads: LoHiLoadsListItem[];
  driverCut: number;
  lastLocation?: {
    latitude: number;
    longitude: number;
  };
  rewardTierHistory: RewardTierHistory[];
  userTierEvent: UserTierEvent[];
  homebaseLng: number;
  homebaseLat: number;
  isClassicDriver: boolean;
}

export interface DriverCertification {
  id: number;
  name: string;
  description: string;
}

export interface HSEScore {
  UserID: string;
  Score: number;
  IsSuspended: boolean;
  ViolationsCount: number;
}

export interface DriverHSEScore {
  score: {
    value: string;
    status: string;
    title: string;
    description: string;
    isSuspended: boolean;
  };
  violations: {
    shiftId: string;
    shiftStartAt: string;
    shiftEndAt: string;
    shiftStartLng: number;
    shiftStartLat: number;
    violations: {
      startAt: string;
      expiredAt: string;
      scores: number;
      type: string;
      name: string;
    }[];
  }[];
}

export interface InternalDriverListItem {
  id: string;
  name: string;
  phone: string;
  driver: boolean;
  admin: boolean;
  companyName?: string;
}

export interface Experience {
  id: string;
  name: string;
  description: string;
  minimumYears: number;
  recommendedYears: number;
  users: string[];
  payloads: {
    payloadTypeId: string;
    payloadTypeName: string;
  }[];
}

export interface PayloadType {
  id: string;
  name: string;
}

export interface WebBanner {
  text: string;
  textColor: string;
  backgroundColor: string;
}

export interface OwnerDashboardStats {
  pastRevenue: number;
  lastSettlement: number;
  loadsNeedAuditing: number;
}

export interface TodayDemand {
  regionName: string;
  demandLevel: 'low' | 'medium' | 'high';
  regionId: number;
  pennyPerShift: number;
  yourDrivers: number;
  demandLevelNextWeek: 'low' | 'medium' | 'high';
  deadheadPaid: boolean;
  driversNeededToday: number;
  driversNeededNextWeek: number;
  regionLocation?: {
    latitude: number;
    longitude: number;
  };
}

export interface FracActivityReportPerRegion {
  regionId: number;
  regionName: string;
  fracsRunningToday: FracActivityInfo[];
  fracsDown: FracActivityInfo[];
  fracsStartingSoon: FracActivityInfo[];
  fracsLongWaitTimes: FracActivityInfo[];
  fracsPumpingSlower: FracActivityInfo[];
  avgTurnRate: number;
}

export interface FracActivityReportPerRegionWithQualifiedInfo {
  qualifiedFracs: FracActivityReportPerRegion[];
  nonQualifiedFracs: FracActivityReportPerRegion[];
}

export interface FracActivityInfo {
  name: string;
  completedPercentage: number;
  lngLat: LongLat;
  established?: string;
  timeRemainingMinutes?: number;
  waitTimeInMinutes?: number;
  trailerTypeName?: string;
}

export interface TrailerStatus {
  availableTrailers: { trailerId: string; trailerName: string }[];
  allTrailers: {
    trailerId: number;
    trailerName: string;
    ownershipType: string;
    availableToDriver: boolean;
    agnusStatus?: {
      trailerId: 17142;
      trailerName: string;
      archivedAt: string;
      operationalStatus: string;
      orderId: number;
      userName: string;
      userPhone: string;
    };
  }[];
}

export interface Region {
  id: number;
  name: string;
  beamerSegmentFilter: string;
}

export interface DriverTierFeed {
  userId: string;
  username: string;
  currentTierName: string;
  isDemoted: boolean;
  reason: string;
  shortReason: string;
  createdAt: string;
  points: number;
}

export interface UserTierEvent {
  id: number;
  type: string;
  name: string;
  description: string;
  points: number;
  createdAt: string;
}

export interface UserTierDetail {
  userID: string;
  userName: string;
  userPhone: string;
  tierUserID: number;
}

export interface TierDiverCount {
  tierId: number;
  tierName: string;
  numberOfDrivers: number;
  driverTakeRate: number;
}

export interface TierFeedsResponse {
  driverTierFeeds: DriverTierFeed[];
  tierDiverCount: TierDiverCount[];
}

export interface DriverTransferRequest {
  driverId: string;
  currentRegionId?: number;
  transferDate: string;
  toRegionId: number;
}

export interface DriverWithRegion {
  driverId: string;
  driverName: string;
  regionId?: number;
  regionName?: string;
}

export interface BonusEvent {
  name: string;
  eventType: string;
  startsAt: Date;
  endsAt: Date;
  ordersPricingType: string;
  ordersPricingValue: string;
  ordersPricingMaxValue: string;
  retentionsPricingType: string;
  retentionsPricingValue: string;
}

export interface LastSettlementFees {
  lastSettlementId?: number;
  trailerFees?: TrailerOrShiftFees;
  shiftFees?: TrailerOrShiftFees;
}

export interface TrailerOrShiftFees {
  chargeCount: number;
  totalCostInCents: number;
}

export interface DriversHitchStats {
  drivers: number;
  onHitch: number;
  hasFutureHitch: number;
  excusedDrivers: number;
  offHitch: number;
  noShows: number;
  doesNotHaveFutureHitch: number;
}

export interface CalenderDayHitch {
  startMinutes?: number;
  endMinutes?: number;
  driverName: string;
  regionId: number;
  regionName: string;
  regionTimezone: string;
  shiftStartMinute?: number;
  startDate: string;
  lastDate: string;
  startDateTimezone: string;
  lastDateTimezone: string;
  shiftStartAt: string;
  shiftEndAt: string;
  archivedAt: string;
  isDayShift: boolean;
}

export interface CalendarDayDriver {
  hitchId: number;
  name: string;
  regionId: number;
  regionName: string;
  regionTimezone: string;
  shiftStartMinute?: number;
  startDate: string;
  lastDate: string;
  startDateTimezone: string;
  lastDateTimezone: string;
  archivedAt: string;
  isDayShift: boolean;
}

export interface Event {
  name: string;
  eventType: string;
  startsAt: string;
  endsAt: string;
  ordersPricingType: string;
  ordersPricingValue: number;
  ordersPricingMaxValue?: number;
  retentionsPricingType?: string;
  retentionsPricingValue?: string;
  createdAt: string;
}

export interface DriversHitchCalendarDay {
  date: string;
  hitchesFullDay: CalenderDayHitch[];
  hitchesStartingToday: CalenderDayHitch[];
  hitchesEndingToday: CalenderDayHitch[];
  noShowDrivers: CalendarDayDriver[];
  excusedDrivers: CalendarDayDriver[];
  currentMonth: boolean;
  events: Event[];
  driverStatusCount: DriverStatusCount;
}

export interface DriverStatusCount {
  noShowWithArchived: number;
  noShowWithoutArchived: number;
  excusedWithArchived: number;
  excusedWithoutArchived: number;
}

export interface MetaData {
  url: string;
}

export interface NoShowUser {
  name: string;
  phone: string;
}

export interface Alert {
  id: number;
  name: string;
  companyID: string;
  title: string;
  description: string;
  type: string;
  priority: number;
  metaData: MetaData;
  noShowUsers: NoShowUser[];
  dismissing: boolean;
  dismissed: boolean;
}

export interface DispatchableOrder {
  uniqueLoadID: string;
  productSource: Source;
  shipperName: string;
  earningsCents: number;
  earningsCentsPerMile: number;
  payloads: {
    name: string;
    quantity: number;
    typeName?: string;
    unit: string;
  }[];
  powerUnitOnly: boolean;
  permittedTrailerTypeNames?: string[];
  isHazMat: boolean;
  receivedAt: Date;
  stops: {
    lng: number;
    lat: number;
    city: string;
    state: string;
    arrivalWindowStartTime?: Date;
    arrivalWindowEndTime?: Date;
  }[];
  certification_names: string[];
}

export interface DispatchableDriver {
  userId: string;
  name: string;
  phone: string;
  subcontractorId: string;
  subcontractorName: string;
  currentLng: number;
  currentLat: number;
  milesFromFirstStop: number;
  onShift: boolean;
  minutesLeftOnShift: number;
  onHitch: boolean;
  certificationNames: string[];
  payloadTypeNames: string[];
  canDoLoad: boolean;
  reasonsCantDoLoad: string[];
}

export interface OnSiteAsset {
  id: string;
  name: string;
  status?: 'on_lease' | 'available' | 'reserved' | 'in_maintenance' | 'not_available' | 'ready_for_use' | 'in_custody';
  buyerName?: string;
  companyName?: string;
  squadId?: string;
  squad?: Squad;
}

export interface NeedsInspectionAsset {
  id: string;
  assetNumber: string;
  dropOffAt?: string;
  buyerName?: string;
}

export interface AlertAsset {
  id: string;
  assetNumber: string;
  reason: string;
  lastSiteName?: string;
  lastDriverName?: string;
  lastDriverPhone?: string;
  lastDriverCompany?: string;
  puckEnabled: boolean;
}

export interface OutOfServiceAsset {
  id: string;
  assetNumber: string;
  createdAt: string;
  createdBy: string;
  details?: string;
}

export interface StackedAsset {
  id: string;
  assetNumber: string;
  createdAt: string;
  createdBy: string;
  details?: string;
}

export interface AssetContract {
  id: string;
  name: string;
}

export interface LamAsset {
  id: string;
  name: string;
}

export interface CreateWO {
  create: boolean;
}

export interface Driver {
  id: string;
  name: string;
  phone: string;
}

export interface Contract {
  id: string;
  name: string;
  type: string;
  companyId: string;
  isListed: boolean;
  version: number;
  originalContractId?: string;
  dayRate?: DayRate;
  perLoadPercent?: PerLoadPercent;
  leaseToOwn?: LeaseToOwn;
  lateFeeEnabled: boolean;
  lateFeeGraceHours?: number;
  lateFeeDailyCents?: number;
  trailerType?: TrailerTypeContract;
  trailerDamageCharge?: TrailerDamageCoverage;
}

export interface ContractWithAssets extends Contract {
  assets?: Asset[];
}

export interface TrailerDamageCoverage {
  id: string;
  name?: string;
  trailerType?: string;
  trailerTypeId?: number;
  cents?: number;
}

export interface PerLoadPercent {
  contractId?: string;
  percent: number;
  minimumDayRateCents?: number;
  maximumMonthlyCents?: number;
}

export interface DayRate {
  contractId?: string;
  cents: number;
}

export interface LeaseToOwn {
  contractId?: string;
  companyId?: string;
  assetId?: number;
  initialBalanceCents: number;
  dailyDueCents: number;
}

export interface ContractRequestBase {
  name: string;
  type: string;
  dayRate?: DayRate;
  perLoadPercent?: PerLoadPercent;
  leaseToOwn?: LeaseToOwn;
  trailerType?: TrailerTypeContract;
  trailerDamageCharge?: TrailerDamageCoverage;
}

export type ContractRequestFees =
  | { lateFeeEnabled: false; lateFeeGraceHours: null; lateFeeDailyCents: null }
  | { lateFeeEnabled: true; lateFeeGraceHours: number; lateFeeDailyCents: number };

export type ContractRequest = ContractRequestBase & ContractRequestFees;

export interface TrailerDamageCoverageRequest {
  name: string;
  trailerTypeId: string;
  cents: number;
}

export interface TrailerTypeContract {
  id: number;
  name: string;
}

export enum RateTypeEnum {
  'day_rate' = 'day_rate',
  'per_load_percent' = 'per_load_percent',
  'lease_to_own' = 'lease_to_own',
}

export enum RateTypeEnumAsset {
  'daily' = 'daily',
  'lease_to_own' = 'lto',
  'per_load' = 'per_load',
}

export interface RevenueHistory {
  reservations?: YMTAssetReservation[] | null;
  timeline?: YMTTimelineEntry[] | null;
  lastOrderInfo?: YMTLastOrder;
  netRevenue: number;
  netRevenue30Days: number;
  netRevenue60Days: number;
  usageLast60Days: number;
  usageLast30Days: number;
}

export interface YMTLastOrder {
  endTimestamp: string;
  orderId: number;
  userName: string;
  userPhone: string;
  orderStatus: string;
  siteLocation: number[];
  siteName: string;
  subContractorName: string;
  uniqueOrderId: string;
}

export interface AssetNote {
  id: number;
  note: string;
  createdBy: string;
  createdAt: string;
}

export interface YMTNameIdLocation {
  id: number;
  name: string;
  createdByName?: null;
  lastKnowLocation?: Location;
  reservedByName?: string;
  status: string;
  agnusAssetId: number;
  listingPrice: string;
  droppedSiteName: string;
  gpsUpdatedAt: string;
  locationType: string;
  pickupSiteName: string;
  subContractorName: string;
  rateType: string;
  siteName: string;
  gpsConnected: boolean;
  inYard: boolean;
  workingStatus: string;
  missingReportedAt: string;
  missingReportedBy: string;
  details: AssetDetails;
  type: string;
  trailerType: string;
  description: string;
  make: string;
  model: string;
  vin: string;
  year: number;
  boxTechnologies: BoxTechnology[];
  externalIdentifier: string;
  notes?: AssetNote;
  internalNotes?: AssetNote;
  fleetioId: string;
  isFFAsset: boolean;
  companyName: string;
}

export interface YMTLastOrder {
  endTimestamp: string;
  orderId: number;
  userName: string;
  userPhone: string;
  orderStatus: string;
  siteLocation: number[];
  siteName: string;
  subContractorName: string;
  uniqueOrderId: string;
}

export interface YMTAssetReservation {
  reservedByName: string;
  reservedBy: string;
  pickedUpAt: string;
  dropOffAt?: string | null;
}

export interface YMTTimelineEntry {
  description: string;
  endDescription?: string;
  startsAt: string;
  endsAt?: string;
  now: boolean;
  revenueSumInCents?: number;
  type: string;
  maintenance: {
    description: string[];
    total: string;
  };
  subEvents: YMTTimelineEntry[];
}

export interface Location {
  latitude: number;
  longitude: number;
}

export interface WorkOrder {
  // will come in work order lists
  id: string;
  milesIn?: number;
  milesOut?: number;
  maintenanceType: string;
  scheduledAt?: string;
  completed_at?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  asset_id: number;
  assetName: string;
  assetType: string;
  licensePlate: string;
  completedByName?: string;
  scheduledByName?: string;
  workOrderSiteName: string;
  numberOfImages: number;
  lastChangeTimestamp: string;
  siteId: string;

  // will come in single work order
  driverName?: string;
  driverPhone?: string;
  // reservation?: Reservation;
  asset?: LAMAsset;
  completedBy?: string;
  scheduledBy?: string;
  uploadedImageURLs?: string[];
  comments?: Comment[];
  statusHistory?: StatusHistory[];
  createdAtSite?: CreatedAtSite;
  completedImageUrl?: string;
  CompletedImageUrls?: string[];

  // will be populated locally to add comment and status in one
  activityLogs?: ActivityLog[];

  // when marking as complete, add image id
  completeImageId: number[];

  completedSapId?: string;
  maintenanceCost: number;
  uploadedImageDetails?: ImageDetail[];
}

export interface ImageDetail {
  uploadedBy: string;
  userId: string;
  companyName: string;
  uploadedAt: string;
  url: string;
}

interface StatusHistory {
  id: number;
  oldStatus: string;
  newStatus: string;
  changedAt: string;
  changedById: string;
  changedByName: string;
}

interface CreatedAtSite {
  id: number;
  name: string;
  location: Location;
  radius: number;
  type: string;
  address?: string;
}

interface ActivityLog {
  type: 'comment' | 'history';
  data: Comment | StatusHistory;
  time: string;
}

export interface LAMAsset {
  id?: string;
  name: string;
  type: 'trailer' | string;
  createdAt?: string;
  currentSite?: any;
  lastKnownLocation?: Location;
  licensePlate: string;
  make?: string;
  model?: string;
  state?: string;
  // @ts-ignore
  status?: string;
  updatedAt?: string;
  vin?: string;
  company?: any;
}

// export interface Reservation {
//   id: string;
//   checkInTime: string;
//   checkOutTime: string;
//   status: 'pending' | 'approved' | 'picked_up' | 'dropped';
//   pricePerDay: number;
//   createdAt: string;
//   reservedBy: ReservedBy;
//   assets: Asset[];
//   consumer: Consumer;
//   comments?: Comment[];
//   supplier: Consumer;
//   listing: AssetListing;
//   assetsRequired: number;
//   listingImageUrls: ImageUrls[];
//
//   toPickupAsset: Asset;
//   toDropOffAsset: Asset;
//   assetListingRate: AssetListingRate;
// }
//
// export interface ReservedBy {
//   id: string;
//   name: string;
// }

export interface TotalEarned {
  pastDay: number;
  past7Days: number;
  past30Days: number;
  past60Days: number;
  past90Days: number;
}

export interface UtilizationRate {
  last3Months: number;
  last6Months: number;
  lastYear: number;
}

export interface AssetStatus {
  withDriver: number;
  atYard: number;
  maintenance: number;
}

export interface AssetDashboardStats {
  totalEarned: TotalEarned;
  utilizationRate: UtilizationRate;
  assetStatus: AssetStatus;
  assetStatusByRegionId: any;
}

export interface Asset {
  id: number;
  name: string;
  type: string;
  status?: string;
  isListed: boolean;
  trailerType: string;
  isFFAsset: boolean;
  details: AssetDetails;
  lastLocation?: LastLocation;
  companyName?: string;
  primaryStatusId?: number;
  primaryStatusType?: string;
  primaryStatusName?: string;
  secondaryStatusId?: number;
  secondaryStatusType?: string;
  secondaryStatusName?: string;
}

export interface LastLocation {
  regionId: number;
  regionName: string;
  lastLocationType: string;
  lastLocationTime?: string;
}

export interface AssetDetails {
  agnusAssetId: number;
  reservedByName?: string;
  reservedByPhone?: string;
  reservedByCompany: string;
  lastKnowLocation: Location;
  gpsUpdatedAt?: any;
  gpsConnected: boolean;
  site: Site;
  contract?: Contract;
  revenueHistory?: RevenueHistory;
  inYard: boolean;
  contractId: string;
  isFFAsset: boolean;
}

export interface Location {
  longitude: number;
  latitude: number;
}

export interface Site {
  id: string;
  name: string;
  address: string;
  location: Location;
  stateId?: any;
  state?: any;
  canDoLightMAndR: boolean;
  canDoHeavyMAndR: boolean;
  supportPickups: boolean;
  supportDropOffs: boolean;
  manned: boolean;
  gated: boolean;
  isExternal: boolean;
  autoReadyForUse: boolean;
  sharedWithCompanies?: any;
  operationalHours?: any;
  staffedHours?: any;
  siteManagerName: string;
  siteManagerEmail: string;
  siteManagerPhone: string;
}

export interface AssetListing {
  id: string; // ; `json:"id" db:"id"`
  name: string; // `json:"name" db:"name"`
  typeId?: number; //  `json:"typeId" db:"type_id"`
  subTypeId?: number; //  // integer references lmp_asset_sub_type(id),
  description?: string; // //varchar,
  manufacturer?: string; // //varchar,
  yearOfManufacturing?: number; //  //integer,
  length?: number; //  //integer,
  width?: number; //  //integer,
  suspensionType?: string; // //varchar,
  compositionType?: string; // //varchar,
  pickupSiteId?: string; //  //integer references lmp_site(id),
  dropOffSiteId?: string; //  //integer references lmp_site(id),
  maintenanceTerms?: string; // // varchar,
  optionToBuy?: boolean;
  buyOutPrice?: number; // //integer,
  rateType?: string;
  dayRate?: number;
  fixedMinDayRate?: number;
  maxRentalPeriod?: number;
  miscRate?: number;
  assetListingRate?: AssetListingRate;
  assetIds?: string[]; //
  imageIds?: string[]; //
  selfDeclaration?: boolean;
  assets?: Asset[];
  imageUrls?: any[];
  pickupSite?: Site;
  dropOffSite?: Site;
  assetsAvailable?: number;
  type?: AssetType;
  subType?: AssetSubType;
  tractorType?: TractorType;
  tractorSubType?: TractorSubType;
  numberOfRequests?: number;
  status?: 'active' | 'deactivate';
  distanceFromPickup?: number;
  dropoffSite?: Site;
  reservations?: Reservation[];
  reservationsPendingCount?: number;
  supplierUserId?: string;
  averageRentalPrice?: number;
  averageRentalUtilization?: number;
  leasedAssets?: number;
  createdAt?: string;
  isPrivate?: boolean;
  engine?: string;
  transmission?: string;
  fuel?: string;
  mileage?: number;
  exteriorConditionId?: string;
  interiorConditionId?: string;
  listingType?: 'truck' | 'trailer';
  tractorListingDetails?: TractorListingMoreDetails[];
  oneTimeEscrow?: number;
  totalEscrow?: number;
}

export interface TractorListingMoreDetails {
  description?: string;
  detailsId?: number;
  detailsName?: string;
}

export interface TractorType {
  id: number;
  name: string;
  assetSubTypes: TractorSubType[];
}

export interface Reservation {
  id: string;
  checkInTime: string;
  checkOutTime: string;
  status: 'pending' | 'approved' | 'picked_up' | 'dropped';
  pricePerDay: number;
  createdAt: string;
  reservedBy: ReservedBy;
  assets: Asset[];
  consumer: Consumer;
  comments?: Comment[];
  supplier: Consumer;
  listing: AssetListing;
  assetsRequired: number;
  listingImageUrls: ImageUrls[];

  toPickupAsset: Asset;
  toDropOffAsset: Asset;
  assetListingRate: AssetListingRate;
}

export interface ReservedBy {
  id: string;
  name: string;
}

export interface ImageUrls {
  id: string;
  url: string;
}

export interface Consumer {
  id: string;
  logoUrl?: any;
  name: string;
  website: string;
  address: string;
  description: string;
  regions?: any;
}

export interface TractorSubType {
  id: number;
  name: string;
}

export interface AssetListingDraft {
  id?: string;
  name: string;
  listingType?: 'truck' | 'trailer';
  draft: string;
}

export interface AssetListingRate {
  id?: number;
  rateType: string;
  dayRate?: number;
  fixedMinimumDayRate?: number;
  minimumRentalPeriod?: number;
  upFrontPay?: number;
  createdAt?: string;
  updatedAt?: string;
  perLoadRate?: boolean;
  perLoadPercentage?: number;
  longTermDiscount?: boolean;
  longTermDiscountPercentage?: number;
  maximumMonthlyPrice?: number;
  initialBalance?: number;
  dailyDue?: number;
}

export interface AssetSubType {
  id: number;
  name: string;
  assetTypeId: number;
}

export interface AssetType {
  id: number;
  name: string;
  assetSubTypes: AssetSubType[];
}

export interface AssetListingWithSite extends AssetListing {
  site?: Site;
  isLoading: boolean;
}

export interface State {
  id: number;
  name: string;
}

export interface FuelCardListItem {
  cardId: string;
  driverName: string;
  lastUsedAt?: string;
}

export interface SHIntakeDriver {
  id: string;
  name: string;
  phone: string;
  status: string;
  driverCertificationStatus: string;
  haulingExperienceStatus: string;
}

export interface SHIntakeTrailerType {
  id: number;
  name: string;
}

export interface CDLEndorsement {
  endorsementIds: number[];
  expirationDate: string;
  documentIds: number[];
}

export interface Certification {
  certificationTypeId: number;
  documentId: number;
  expirationDate: string;
}

export interface HaulingExperience {
  haulingExpTypeId: number;
  yearsOfExperience: number;
  trailerTypeIds: number[];
}

export interface HaulingExperienceResponse {
  id: number;
  name: string;
  description: string;
}

export interface OtherHaulingExperience {
  name: string;
  yearsOfExperience: number;
  trailerTypeIds: number[];
}

export interface DriverCertificationForm {
  userId: string;
  cdlEndorsements: CDLEndorsement;
  driverCertifications: Certification[];
  haulingExperiences: HaulingExperience[];
  otherHaulingExperiences: OtherHaulingExperience[];
}

export interface Doc {
  id: number;
  name: string;
  type: string;
}

export interface CertificationResponse {
  id: number;
  name: string;
  expiresAt: Date;
  description: string;
  docs: Doc[];
}

export interface TrailerType {
  id: string;
  name: string;
}

export interface HaulingExperienceResponse {
  id: number;
  name: string;
  yearsOfExperience: number;
  workHistoryId?: number;
  workHistoryName: string;
  trailerTypes: TrailerType[];
}

export interface DriverCertificationResponse {
  certifications: CertificationResponse[];
  haulingExperiences: HaulingExperienceResponse[];
}

export interface AssetMaintainerEmail {
  id: string;
  name: string;
  email: string;
  companyId: string;
  createdAt: string;
  regions: Region[];
}

export interface BoxTechnology {
  id: number;
  name: string;
}

export interface KeyValuePair {
  key: string;
  value: string;
}

export interface FuelCardTransaction {
  id: number;
  driverName: string;
  transactionDate: string;
  fuelType: string;
  quantity: number;
  cardNumber: number;
  amount: number;
  paidAmount?: number;
  settlementId?: number;
  settledOn?: string;
}

export interface CarrierQualificationQuestionCategory {
  categoryName: string;
  questions: CarrierQualificationQuestion[];
}

export interface CarrierQualificationQuestion {
  question: string;
  answerFormControlName: string;
  showSubQuestionIf?: boolean;
  subQuestion?: string;
  subQuestionAnswerFormControlName?: string;
  secondSubQuestion?: string;
  secondSubQuestionAnswerFormControlName?: string;
}

export interface CarrierQuestion {
  category: string;
  question: string;
  answer: string;
  firstSubQuestion?: string;
  firstSubQuestionAnswer?: string;
  secondSubQuestion?: string;
  secondSubQuestionAnswer?: string;
}

export interface LoHiPayTransaction {
  id: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  initator: { id: string; name: string };
  merchantCompany: { id: string; name: string };
  consumerUser: { id: string; name: string };
  company: { id: string; name: string };
  description: string;
  totalCents: number;
  approver: { id: string; name: string };
  approvedAt: string | null;
  rejecter: { id: string; name: string };
  rejectedAt: string | null;
  merchantsInternalId: string | null;
  settlementId: string | null;
}

export interface LoHiPayOneTimeCode {
  id: number;
  code: string;
  company: { id: string; name: string };
  user: { id: string; name: string };
  expiresAt: string;
  availableCreditCents: number;
  merchantCompany: { id: string; name: string };
  merchantUser: { id: string; name: string };
  transaction?: LoHiPayTransaction;
}

export interface LoHiPayOneTimeCodeMerchant {
  id: number;
  code: string;
  company: { id: string; name: string };
  user: { id: string; name: string };
  expiresAt: string;
}

export interface LoHiPayMerchant {
  id: string;
  name: string;
  totalPaidCents: number;
}

export interface LoHiPayServerSentEvent {
  dataType: 'oneTimeCode' | 'transaction';
  data: LoHiPayOneTimeCode | LoHiPayTransaction;
}

export interface LoHiPaySettlement {
  id: number;
  transactionCount: number;
  totalSettledCents: number;
  dateOfCreation: string;
  paidAt: string;
}

export interface LoHiPaySettlementDetails extends LoHiPaySettlement {
  transactions: LoHiPayTransaction[];
}

export interface SHBannerLoadsCount {
  count: number;
}

export interface PointOfInterest {
  id: number;
  type: 'maintenance_shop' | '1f_yard';
  name: string;
  address: string;
  lnglat: Location;
  phone: string;
  description: string;
  displayOnMap: boolean;
}

export interface Question {
  id: number;
  groupType: string;
  question: string;
  parentAnswerOptionId: number;
  parentQuestionId: number;
  questionType: string;
  answerOptions: AnswerOption[];
  selectedAnswer: SelectedAnswer;
}

export interface AnswerOption {
  id: number;
  questionId: number;
  answer: string;
  extraInformationNeeded: boolean;
  extraInformation: string;
  imageUploadEnabled: boolean;
  selectDrivers: boolean;
}

export interface SelectedAnswer {
  id: number;
  userId: string;
  questionId: number;
  answer: string;
  answerId: string;
  extraInformation: string;
  answerDocuments: AnswerDocument[];
  answerDrivers: AnswerDriver[];
}

export interface AnswerDocument {
  id: number;
  answerId: number;
  url: string;
}

export interface AnswerDriver {
  id: number;
  answerId: number;
  driverId: string;
}

export interface QuestionnaireWithAnswer {
  questionId: number;
  answerId?: number;
  extraInformation?: string;
  documentIds?: number[];
  driverIds?: string[];
}

export interface BaldiniDateFormatTM {
  year: number;
  month: number;
  day: number;
}

export interface ClientError {
  isClientError: boolean;
  messageToUser: string;
}

export interface LoHiPayCreditLineInfo {
  escrowTotalCents: number;
  creditLineCents: number;
  creditAvailableCents: number;
}

export interface LastSettlement {
  dateOfCreation?: Date;
  payableInCents?: number;
}

export interface DriverSupplyCompany {
  id: string;
  name: string;
  billingSubsidiary: string;
  ownerName: string;
  ownerPhone: string;
  activeDrivers: number;
  driversChurnedPast30Days: number;
  aTeam: boolean;
  earningsPerShiftPast30Days: number;
  earningsPerShiftPercentilePast30Days: number;
  currentBillingDisputesCount: number;
  regionId: number;
  regionName: string;
  contactPreference: string;
  currentBillingDisputesTotalAmount: number;
  lastSettlement: LastSettlement;
}

export interface SettlementStatementSummarySingleDriver {
  id: string;
  date: string;
  paidAt: string;
  fuelTotalCents: number;
  loadCount: number;
  loadTotalCents: number;
  takeRateTotalCents: number;
  itemCents: number;
}

export interface SettlementStatementFuelTransactions {
  id: number;
  time: string;
  totalInPennies: number;
}

export interface SettlementStatementItem {
  id: number;
  totalCents: number;
  displayName: string;
}

export interface UpdateCompanyDetailsRequestData {
  isATeam: boolean;
  companyId: string;
}

export interface SettlementStatementLoadLineItem {
  description: string;
  totalCents: number;
  isBonus: boolean;
}

export interface SettlementStatementLoad {
  id: string;
  settledOn: Date;
  completedAt: Date;
  settledAmountCents: number;
  settledAmountNoFC: number;
  settledAmountBonus: number;
  settlementId: number;
  lineItems: SettlementStatementLoadLineItem[];
}

export interface SettlementStatementBonus {
  date: Date;
  description: string;
  totalCents: number;
}

export interface TrailerBaseDetails {
  name: string;
  companyName: string;
  status: string;
}

export interface RecoveryAsset {
  id: number;
  status: string;
  assetNumber: string;
  assetType: string;
  reason: string;
  lastSiteName: string;
  lastDriverName: string;
  lastDriverPhone: string;
  lastDriverCompany?: any;
  puckEnabled: boolean;
  lastPuckLocation?: any;
  lastPuckLocationDate?: any;
  recoveryDays: number;
  recoveryPersonId?: string;
  recoveryPersonName?: string;
  recoveryPersonEmail?: string;
  recoveryPersonPhone?: string;
  regionId?: number;
  regionName?: string;
  lastLoadId?: string;
  lastLoadCompletedAt?: string;
  ownerId: string;
  ownerName: string;
  leasedToName?: string;
}

export interface RegionWiseStat {
  regionId: number;
  regionName: string;
  availableAssets: number;
  unAvailableAssets?: number;
  recoveryDays?: number;
}

export interface YardManagerWiseStat {
  userId: string;
  userName: string;
  availableAssets: number;
  unAvailableAssets?: any;
  recoveryDays?: any;
}

export interface AssetsOfRecoveryByReason {
  reason: string;
  numberOfAssets: number;
}

export interface NeedsRecoveryStats {
  regionWiseStats: RegionWiseStat[];
  yardManagerWiseStats: YardManagerWiseStat[];
  percentageOfPuckAssets: number;
  assetsOfRecoveryByReason: AssetsOfRecoveryByReason[];
}

export interface YardManager {
  id: string;
  name: string;
  phone?: string;
}

export interface AssetOwner {
  id: string;
  name: string;
}

export interface ClassicCarrier {
  id: number;
  name: string;
}

export interface Squad {
  id: string;
  name: string;
  squadPhones: string[];
  squadNames: string[];
}

export interface LongLat {
  longitude: number;
  latitude: number;
}

export interface LamWaypoint {
  LngLat: LongLat;
  EventTimestamp: string;
}

export interface DriverWaypoint {
  id: number;
  speedAccuracy?: any;
  course?: any;
  courseAccuracy?: any;
  altitude?: any;
  altitudeAccuracy?: any;
  floorLevel?: any;
  timestamp: Date;
  lngLat: number[];
  createTime?: any;
  last: boolean;
}

export interface AssetDetailsAfterDropoff {
  waypoints: LamWaypoint[];
  name: string;
  lastDriverId: string;
}

export interface AutoPickupAssets {
  assetName: string;
  reservedByName?: string;
  reservedByPhone?: string;
  reservedByCompany?: string;
  createdAt: string;
}

export interface TourBrokerCharge {
  id: number;
  loadID: string;
  chargeType: string;
  name: string;
  totalCents: number;
  chargeTypeNameId: number;
  requiresUpload: boolean;
  uploads: TourBrokerChargeUpload[];
}

export interface TourBrokerChargeUpload {
  id: number;
  createdAt: string;
  fileName: string;
  url: string;
}

export interface TourBillingDetails {
  id: string;
  approvedBy: string | null;
  approvedAt: string | null;
  undoExpiresAt: string;
  adminApprovedAt: string | null;
  canUndo: boolean;
}

export interface TourRevenue {
  awaitingInvoicing: number;
  revenueCents: number;
  pastWeekRevenueCents: number;
  twoWeeksAgoRevenueCents: number;
  percentChange: number;

  thisWeekDpmCents: number;
  pastWeekDPMCents: number;
  twoWeeksAgoDPMCents: number;
  dpmPercentChange: number;

  assetRevenue: TourAssetRevenue[];
}

export interface TourAssetRevenue {
  trailerType: SalesTrailerType;
  revenue: number;
}

export interface LohiLoadAllowedChargeType {
  id: number;
  displayName: string;
  requiresUpload: boolean;
}

export interface LmaAssetStatus {
  id: number;
  name: string;
  type: string;
  SecondaryStatues: LmaAssetStatus;
}

export interface RevenueDay {
  date: string;
  settlementDetailId: number | null;
  dedicatedAssetBonusCents: number;
  otherCents: number;
  detentionCents: number;
  // this is the real take rate cents applied to dabs, the other field is the estimated take rate loss by not being the highest tier
  takeRateCents: number;
  loadCents: number;
  dabLossCents: number;
  takeRateLossCents: number;
}

export interface MetricCards {
  totalRevenue: {
    currentValueCents: number;
    previousValueCents: number;
  };
  averageRevenuePerShift: {
    currentValueCents: number;
    previousValueCents: number;
  };
  averageMissedRevenuePerShift: {
    currentValueCents: number;
    previousValueCents: number;
  };
  averageRevenuePerWeek: {
    currentValueCents: number;
    previousValueCents: number;
  };
  averageTurnRatePerShift: {
    currentValue: number;
    previousValue: number;
  };
}

export interface AssetPickupData {
  id: string;
  driverName: string;
  phoneNumber: string;
  companyName: string;
  assetName: string;
  assetId?: number;
  pickupSiteName: string;
  scheduledAt?: string;
  completedAt?: string;
  archivedAt?: string;
}

export type KPIDuration =
  | '1_weeks'
  | '2_weeks'
  | '1_months'
  | '1_quarters'
  | 'last_7_days'
  | 'last_14_days'
  | 'last_30_days';
// eslint-disable-next-line @typescript-eslint/naming-convention
export const KPIDurations: KPIDuration[] = [
  '1_weeks',
  '2_weeks',
  '1_months',
  '1_quarters',
  'last_7_days',
  'last_14_days',
  'last_30_days',
];

export interface Card {
  title: string;
  value: string;
  subtext: string;
  infoIcon: string;
  indicatorIcon: string;
  indicatorText: string;
  textColor: string;
  indicatorClass: string;
}

export const ICONS = {
  information: '/assets/icons/new-dashboard/information.svg',
  upArrowGreen: '/assets/icons/new-dashboard/up-arrow-green.svg',
  bottomArrowRed: '/assets/icons/new-dashboard/bottom-arrow-red.svg',
  upRedArrow: '/assets/icons/new-dashboard/up-red-arrow.svg',
};
